/**
 * Receipt capture helpers for Profit Tracker payment tracking.
 *
 * Receipts are shrunk and re-encoded to JPEG on a canvas in the browser, then
 * stored inline on the week document as data URIs — the same approach the
 * Maintenance Orders tracker uses, since there is no binary upload endpoint
 * for these modules and an inline image is readable without a second
 * authenticated request. The compression is what keeps the Mongo document
 * well clear of the 16MB cap.
 *
 * Receipts are photographed paperwork rather than scenery, so the first step
 * keeps more resolution than the maintenance-photo ladder does — an unreadable
 * total defeats the point of attaching it.
 */

// Per-step budget (a report and a verification each get their own set).
export const MAX_RECEIPTS = 5;
export const RECEIPT_MAX_BYTES = 500 * 1024;
const RECEIPT_STEPS = [
  [1600, 0.72],
  [1300, 0.62],
  [1000, 0.52],
  [800, 0.44],
];

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that file"));
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode that image"));
    img.src = src;
  });
}

// Rough byte count of a base64 payload (4 chars ≈ 3 bytes).
export function dataUrlBytes(dataUrl) {
  const i = String(dataUrl || "").indexOf(",");
  if (i === -1) return 0;
  return Math.round((dataUrl.length - i - 1) * 0.75);
}

function drawToDataUrl(img, maxDim, quality) {
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  // JPEG has no alpha — paint white first so a transparent PNG doesn't go black.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  return { dataUrl: canvas.toDataURL("image/jpeg", quality), width: w, height: h };
}

/**
 * Downscale/re-encode until the result fits RECEIPT_MAX_BYTES. Returns the
 * smallest attempt even if the last step is still too big — the caller decides
 * whether to accept it.
 */
export async function compressReceipt(file) {
  const raw = await readFileAsDataUrl(file);
  const img = await loadImage(raw);
  let last = null;
  for (let i = 0; i < RECEIPT_STEPS.length; i += 1) {
    const [dim, q] = RECEIPT_STEPS[i];
    last = drawToDataUrl(img, dim, q);
    last.bytes = dataUrlBytes(last.dataUrl);
    if (last.bytes <= RECEIPT_MAX_BYTES) return last;
  }
  return last;
}

/**
 * True on a touch device with no precise pointer — the only place the camera
 * button is worth showing. A desktop browser reports `capture` on the input
 * element whether or not a camera exists, so feature-detecting that instead
 * would put a dead button on every laptop.
 */
export function isHandheld() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return (
    window.matchMedia("(pointer: coarse)").matches &&
    window.matchMedia("(hover: none)").matches
  );
}

export const newReceiptId = () =>
  `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
